import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    FeatureValues,
    IFeatureSetting,
    ITenantFeature,
    ODataResponse,
} from '@models';
import { ODataService } from '@services/generics/odata.service';
import { MapperService } from '@services/mapper.service';

import isEqual from 'lodash-es/isEqual';
import { map } from 'rxjs/operators';

@Injectable()
export class FeaturesService extends ODataService<ITenantFeature, string> {

    public static generateFeatureDisplayMap(featuresValues: ITenantFeature[]) {

        FeaturesService._tenantFeatures = featuresValues;
        const featureDisplayMap: Record<string, IFeatureSetting | null> = {};

        featuresValues.forEach((featureValue: ITenantFeature) => {
            const toRemove = FeaturesService._getFeaturesToRemove(featureValue);
            featureValue.featureSettings.forEach((feature: IFeatureSetting) => {
                const displayName = feature.displayName;

                featureDisplayMap[displayName] = toRemove.includes(feature.name) || (featureDisplayMap[displayName] &&
                    !isEqual(featureDisplayMap[displayName], feature)) ? null : feature;
            });
        });

        return featureDisplayMap;
    }

    private static _tenantFeatures: ITenantFeature[] = [];

    private static _getFeaturesToRemove(featureValue: ITenantFeature): string[] {
       let featuresToRemove: string[] = [];
        // If Insights is not enabled, do not show feature flags for specific Insights features
        const filterInsightsDataFeatures = featureValue.featureSettings.reduce((res: boolean, feature: IFeatureSetting) => {
            return res || (feature.name === 'Insights.Enabled' && !feature.value);
        }, false);

        if (filterInsightsDataFeatures) {
            featuresToRemove = [
                'Insights.QueueSpecificData.Enabled',
                'Insights.QueueOutputData.Enabled',
                'Insights.QueueAnalyticsData.Enabled',
            ];
        }

        return featuresToRemove;
    }

    protected _endpoint = '/odata/Features';

    constructor(http: HttpClient) {
        super(http, MapperService.tenantFeatureMap);
    }

    public getFeatureFlagsForTenants(tenantIds: number[]) {
        return this._http
            .post(`${this._endpoint}/UiPath.Server.Configuration.OData.GetFeatureValuesPerTenantBulk`, {
                tenantIds,
            })
            .pipe(
                map((res) => {
                    const mappedData = this._mapData(res) as ODataResponse<ITenantFeature>;
                    return mappedData.data;
                }),
                map(res => FeaturesService.generateFeatureDisplayMap(res)),
            );
    }

    public updateFeatureFlags(featuresToBeUpdated: Record<string, boolean>, tenantIds: number[]) {

        const featureNameByDisplayName: Record<string, string> = {};

        FeaturesService._tenantFeatures.forEach((tenantFeature: ITenantFeature) => {
            tenantFeature.featureSettings.forEach((key: IFeatureSetting) => {
                featureNameByDisplayName[key.displayName] = key.name;
            });
        });

        const featureSettings: { TenantId: number; Name: string; Value: FeatureValues; }[] = [];

        tenantIds.forEach((tenantId: number) => {
            Object.keys(featuresToBeUpdated).forEach((featureDisplayName) => {
                featureSettings.push({
                    TenantId: tenantId,
                    Name: featureNameByDisplayName[featureDisplayName],
                    Value: featuresToBeUpdated[featureDisplayName] ? FeatureValues.True : FeatureValues.False,
                });
            });
        });

        const reqBody = {
            'feature_settings': featureSettings,
        };
        const reqUrl = `${this._endpoint}/UiPath.Server.Configuration.OData.UpdateFeaturesBulk`;
        return this._http.post(reqUrl, reqBody);
    }
}
